import { Elements } from '@stripe/react-stripe-js';
import type { Stripe, StripeElementLocale, StripeElementsOptions } from '@stripe/stripe-js';

import { useQuery } from '@tanstack/react-query';
import { customerControllerGetPaymentMethodSetupSecretQueryOptions } from 'korint-sdk';
import { useTranslation } from 'react-i18next';
import { Spinner } from '../../ui/spinner';
import { CheckoutForm } from './CheckoutForm';
import { Group } from '@folio/components/ui/flex';

export const PaymentMethodSetup = ({
  customerId,
  stripePromise,
  product,
  confirmSuccess,
}: {
  customerId: string;
  stripePromise: Promise<Stripe | null>;
  product?: string;
  confirmSuccess: () => Promise<void>;
}) => {
  const { t, i18n } = useTranslation();
  const { data, status } = useQuery({
    ...customerControllerGetPaymentMethodSetupSecretQueryOptions(
      customerId,
      {},
      { headers: { Product: product } },
    ),
    retryDelay: 500,
    retry: failureCount => failureCount <= 10, // Retry up to 10 times
    gcTime: 0, // Never cache the data
  });

  if (status === 'pending') {
    return (
      <Group align="stretch" justify="center" className="py-24">
        <Spinner />
      </Group>
    );
  }

  if (status === 'error') {
    return (
      <Group align="stretch" justify="center" gap={0} className="py-24">
        {t('customerPaymentMethods.errors.generic')}
      </Group>
    );
  }

  const options: StripeElementsOptions = {
    clientSecret: data.clientSecret,
    appearance: {
      theme: 'flat',
    },
    locale: i18n.language as StripeElementLocale,
  };

  return (
    <Elements stripe={stripePromise} options={options}>
      <CheckoutForm confirmSuccess={confirmSuccess} />
    </Elements>
  );
};
